import { IconButton, Tooltip } from '@dixa/lib-shared-habla';
import { Fragment, MouseEvent, ReactNode } from 'react';

import { useCopy } from 'utils/useCopy';

import styles from './summaryItems.module.scss';

export type SummaryItem = {
  /** Stable key for the segment. */
  readonly key: string;
  /** Rendered content (text, or a link for e.g. a tracking URL). Empty/absent segments are dropped. */
  readonly content?: ReactNode;
  /** When set, renders a copy-to-clipboard button next to the segment. */
  readonly copyText?: string;
  /** When set, shown as a tooltip on hover (e.g. "Order ID" for an opaque id). */
  readonly label?: string;
};

const hasContent = (item: SummaryItem): boolean =>
  item.content !== undefined && item.content !== null && item.content !== '';

type CopyButtonProps = {
  readonly textToCopy: string;
};

const CopyButton = ({ textToCopy }: CopyButtonProps): JSX.Element => {
  const [copy, { status }] = useCopy();

  const handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    copy(textToCopy);
  };

  return (
    <IconButton
      icon={status === 'copied' ? 'done' : 'copy'}
      variant="tertiary"
      onClick={handleClick}
    />
  );
};

type SummaryItemsProps = {
  readonly items: readonly SummaryItem[];
};

export const SummaryItems = ({
  items,
}: SummaryItemsProps): JSX.Element | null => {
  const shown = items.filter(hasContent);
  if (!shown.length) {
    return null;
  }

  return (
    <span className={styles.items}>
      {shown.map((item, index) => (
        <Fragment key={item.key}>
          {index > 0 && <span className={styles.separator}>·</span>}
          <span className={styles.item}>
            <Tooltip content={item.label}>{item.content}</Tooltip>
            {item.copyText && <CopyButton textToCopy={item.copyText} />}
          </span>
        </Fragment>
      ))}
    </span>
  );
};
